import { isAdmin, isAdminTenant, isManager } from '@/models/role';
import { Beacon } from '@/plugins/beacon';
import { StorageService } from '@/services/storage.service';
import store from '@/store/store';
import { setDocumentTitle } from '@/util/i18n/document';
import { setGlobalMode } from '@/util/mode-util';
import { paths } from '@/util/paths';
import { shouldShowUpsellSurfaces } from '@/util/tier-mapping';

let previousRoute = null;

export function getPreviousRoute() {
  return previousRoute;
}

function loginRedirect(to) {
  return {
    path: paths.login,
    // Store the full path to redirect the user after login if route exists
    query: to.name ? { redirect: to.fullPath } : null,
  };
}

/**
 * Global `beforeEach` guard.
 *
 * Invariant: this MUST always end by calling `next` exactly once. If an awaited
 * async step (auth/validate, setGlobalMode, ...) rejects and escapes, vue-router
 * aborts the navigation and `<router-view>` renders nothing — the "blank white
 * page on first load" bug. Every async step is therefore wrapped so a failure
 * resolves to a concrete destination instead of aborting the transition.
 */
export async function authGuard(to, from, next) {
  const isSameRoute = to.name === from.name;

  const isPublic = to.matched.some((record) => record.meta.public);
  const isAdminTenantRole = to.matched.some((record) => record.meta.adminTenant);
  const isAdminRole = to.matched.some((record) => record.meta.admin);
  const isManagerRole = to.matched.some((record) => record.meta.manager);
  const onlyWhenLoggedOut = to.matched.some((record) => record.meta.onlyWhenLoggedOut);
  const loggedIn = !!StorageService.getToken();
  previousRoute = from;

  if (!isPublic && !loggedIn) {
    return next(loginRedirect(to));
  }

  const user = StorageService.getCurrentUser();

  if (isAdminRole) {
    if (!isAdmin(user && user.role)) {
      return next(paths.notFound);
    }
  }

  if (isAdminTenantRole) {
    if (!isAdminTenant(user && user.role)) {
      return next(paths.notFound);
    }
  }

  if (isManagerRole && !isManager(user && user.role)) {
    return next(paths.notFound);
  }

  // Do not allow user to visit login page or register page if they are logged in
  if (loggedIn && onlyWhenLoggedOut) {
    return next('/');
  }

  // Skip expensive async ops for same-route query/param changes
  if (!isSameRoute) {
    try {
      await setGlobalMode(to);
    } catch (error) {
      // A failed mode switch must never abort the navigation (blank page). The
      // page can recover its mode on the next interaction; carry on rendering.
      console.error('Navigation guard: setGlobalMode failed', error);
    }
  }

  // Set title (if page has one)
  setDocumentTitle(to.meta.title);

  if (loggedIn) {
    // If user is already stuck in not-found page
    if (to.path === paths.notFound && from.path === paths.root) return next();

    if (!isSameRoute) {
      try {
        await store.dispatch('auth/validate');
      } catch (error) {
        // auth/validate already logs the user out on failure. Previously this
        // rejection escaped the guard, aborting the navigation and leaving a
        // blank page (see AxiosError "Request aborted" on cold first loads).
        // Send the user to login instead so the app always renders something.
        console.error('Navigation guard: auth/validate failed', error);
        return next(loginRedirect(to));
      }

      try {
        await store.dispatch('tenantModules/getTenantModules');
        const tenantModules = store.getters['tenantModules/tenantModules'];
        const requiredPermissions = to.meta.permissions;
        if (requiredPermissions && requiredPermissions.length > 0) {
          const hasRequiredPermissions = requiredPermissions.every((p) => tenantModules.includes(p));
          if (!hasRequiredPermissions) {
            const upsellOnMissing = to.matched.some((record) => record.meta && record.meta.upsellOnMissing);
            if (upsellOnMissing && shouldShowUpsellSurfaces()) {
              return next();
            }
            return next(paths.notFound);
          }
        }
      } catch (error) {
        console.error('Navigation guard: tenantModules fetch / permission check failed', error);
        return next(paths.notFound);
      }
    }
  }

  next();

  // Tells Beacon the URL changed
  Beacon.updateUrl(to.fullPath, to.name);
}
